import React, { useContext, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, UserCircle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FamilyContext } from '../../Context/FamilyHeadContext';
import { API_URL } from "../../services/apiService";
import {useTranslation} from "react-i18next";

const MobileFamilyTreeDropdown = ({ onMobileMenuClose }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const scrollableRef = useRef(null);
    const navigate = useNavigate();
const { t, i18n } = useTranslation()
    const {
        familyHeadsBazach,
        familyHeadsHumenir,
        currentFamilyHead,
        loading,
        huminerMain,
        bazachMain
    } = useContext(FamilyContext);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!scrollableRef.current || !isDropdownOpen) return;

        const scrollableArea = scrollableRef.current;

        const handleWheel = (e) => {
            const rect = scrollableArea.getBoundingClientRect();
            const isMouseInside = (
                e.clientX >= rect.left &&
                e.clientX <= rect.right &&
                e.clientY >= rect.top &&
                e.clientY <= rect.bottom
            );

            if (isMouseInside) {
                e.stopPropagation();
            }
        };

        document.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            document.removeEventListener('wheel', handleWheel);
        };
    }, [isDropdownOpen]);

    const getModifiedHeads = (heads, isHuminer) => {
        return heads.map((head) => ({
            ...head,
            path:
                head.gedId === (isHuminer ? huminerMain?.gedId : bazachMain?.gedId)
                    ? isHuminer
                        ? `${i18n.language}/huminer`
                        : `${i18n.language}/`
                    : isHuminer
                        ? `${i18n.language}/huminer/${head.nickname}`
                        : `${i18n.language}/${head.nickname}`,
        }))
    };

    const getAllHeads = () => {
        return [
            ...getModifiedHeads(familyHeadsBazach, false),
            ...getModifiedHeads(familyHeadsHumenir, true)
        ].sort((a, b) => {
            if (a.isMain && !b.isMain) return -1;
            if (!a.isMain && b.isMain) return 1;
            return a.name.localeCompare(b.name);
        });
    };

    const handleSelectFamilyHead = (head) => {
        navigate(head.path);
        setIsDropdownOpen(false);
        onMobileMenuClose?.();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-2">
                <div className="w-5 h-5 border-2 border-[#8B5E3C] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div ref={dropdownRef} className="relative w-full z-50">
            <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex w-full items-center justify-center gap-2 px-4 py-2 rounded-lg hover:bg-[#E6D5C1] transition-colors duration-300 text-[#8B5E3C] font-heebo"
            >
                {currentFamilyHead && (
                    <div className="w-6 h-6 flex-shrink-0">
                        {currentFamilyHead.images?.[0]?.key ? (
                            <img
                                crossOrigin="use-credentials"
                                src={`${API_URL}/image/download/${encodeURIComponent(currentFamilyHead.images[0].key)}`}
                                alt={currentFamilyHead.name}
                                className="w-full h-full rounded-full object-cover"
                            />
                        ) : (
                            <UserCircle className="w-full h-full" />
                        )}
                    </div>
                )}
                <span className="font-bold text-base">{t('navbar.ancestryChoose')}</span>
                {isDropdownOpen ? (
                    <ChevronUp className="w-4 h-4" />
                ) : (
                    <ChevronDown className="w-4 h-4" />
                )}
            </button>

            {isDropdownOpen && (
                <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[1000]" onClick={() => setIsDropdownOpen(false)} />
            )}

            <div className={`fixed inset-0 bg-white rounded-none shadow-xl z-[1001] transform transition-all duration-300 
                ${isDropdownOpen ? 'translate-x-0' : 'translate-x-full opacity-0 pointer-events-none'}`}>
                <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between p-4 border-b">
                        <h2 className="text-lg font-bold text-[#5C4033]">{t('navbar.ancestryChooseTitle')}</h2>
                        <button onClick={() => setIsDropdownOpen(false)}
                                className="p-2 hover:bg-[#f8f5f1] rounded-full text-[#8B5E3C] hover:text-[#5C4033] transition-colors">
                            <X className="w-7 h-7" />
                        </button>
                    </div>

                    <div ref={scrollableRef} className="flex-1 overflow-y-auto">
                        <div className="p-3 grid gap-2">
                            {getAllHeads().map((head) => (
                                <button
                                    key={head.gedId}
                                    onClick={() => handleSelectFamilyHead(head)}
                                    className={`flex items-center gap-3 p-3 rounded-lg w-full text-right transition-all duration-300
                                        ${currentFamilyHead?.gedId === head.gedId
                                        ? 'bg-[#f8f5f1] text-[#4a3f30]'
                                        : 'hover:bg-[#f8f5f1] text-[#8B5E3C]'}`}
                                >
                                    <div className="flex-shrink-0">
                                        {head.images?.[0]?.key ? (
                                            <img
                                                crossOrigin="use-credentials"
                                                src={`${API_URL}/image/download/${encodeURIComponent(head.images[0].key)}`}
                                                alt={head.name}
                                                className="w-12 h-12 rounded-lg object-cover"
                                            />
                                        ) : (
                                            <UserCircle className="w-12 h-12" />
                                        )}
                                    </div>
                                    <div className="flex-grow">
                                        <div className="font-bold">{head.name}</div>
                                        {head.title && (
                                            <div className="text-sm opacity-75">{head.title}</div>
                                        )}
                                    </div>
                                    {head.isMain && (
                                        <span className="text-xs bg-[#E6D5C1] text-[#5C4033] px-2 py-1 rounded-full">
                                            {t('mainLineage.isMain')}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MobileFamilyTreeDropdown;